import { Menu, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { SearchDialog } from '#app/components/search-dialog.js'
import { ServiceMenu } from '#app/components/service-menu.js'
import { Logo, LogoWithName } from '#app/components/ui/logo-icon.js'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover.js'

export function Navbar({
	isSignedIn,
	authUrl,
}: {
	isSignedIn: boolean
	authUrl: string
}) {
	const [searchOpen, setSearchOpen] = useState(false)
	// Kortkommandot visas först efter montering: plattformen (⌘ eller Ctrl) går
	// inte att veta vid SSR.
	const [shortcutLabel, setShortcutLabel] = useState<string | null>(null)

	// ⌘K / Ctrl+K öppnar sökdialogen, som på internetmedicin.se.
	useEffect(() => {
		const isMac = /Mac|iPhone|iPad/.test(navigator.platform)
		setShortcutLabel(isMac ? '⌘K' : 'Ctrl K')
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key?.toLowerCase() !== 'k' || e.altKey || e.shiftKey) return
			if (!(isMac ? e.metaKey : e.ctrlKey)) return
			e.preventDefault()
			setSearchOpen(true)
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [])

	return (
		<>
			<nav className="sticky top-0 z-50 flex-none bg-white/50 backdrop-blur-md dark:bg-black/50 lg:bg-white lg:dark:bg-gray-800 print:hidden">
				<div className="mx-auto flex h-14 max-w-full items-center justify-between px-4 lg:h-16 lg:border-b lg:px-8 lg:dark:border-gray-800">
					{/* Left: Logo */}
					<div className="flex shrink-0 items-center lg:w-60">
						<a
							href="https://internetmedicin.se"
							aria-label="Internetmedicin"
							className="flex items-center rounded-full focus:outline-none focus:ring-0 focus-visible:ring-2 lg:rounded-lg"
						>
							<Logo className="h-8 w-8 lg:hidden" />
							<LogoWithName className="hidden h-auto text-black dark:text-gray-200 lg:block lg:w-[230px]" />
						</a>
					</div>

					{/* Center: Search bar. Bara "Sök": AI-svaret får man i chattrutan
					    nedanför, så knappen lovar inte "ställ en fråga" som sajtens. */}
					<div className="ml-6 mr-3 flex flex-grow lg:mx-0 lg:max-w-[50rem]">
						<button
							type="button"
							aria-label="Sök"
							aria-keyshortcuts="Meta+K Control+K"
							onClick={() => setSearchOpen(true)}
							className="relative flex w-full items-center rounded-full bg-green-100 px-1.5 py-1 text-base text-gray-600 transition hover:text-gray-800 focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:focus:ring-gray-600 lg:rounded-lg lg:px-4 lg:py-2.5 lg:text-lg"
						>
							<Search
								className="ml-2 mr-4 block h-5 w-5 shrink-0 text-gray-600 dark:text-gray-400 lg:ml-0"
								aria-hidden="true"
							/>
							<span className="block text-left">Sök</span>
							{shortcutLabel && (
								<kbd
									aria-hidden="true"
									className="ml-auto hidden rounded border border-gray-300 px-1.5 py-0.5 font-sans text-xs text-gray-500 dark:border-gray-500 dark:text-gray-400 lg:inline-block"
								>
									{shortcutLabel}
								</kbd>
							)}
						</button>
					</div>

					{/* Right: Hamburger menu */}
					<div className="flex shrink-0 items-center justify-end gap-3 lg:w-60">
						<Popover>
							<PopoverTrigger asChild>
								<button
									type="button"
									className="-mr-3 rounded-full p-2 text-gray-500 transition hover:text-gray-700 focus:outline-none focus:ring-2 dark:text-gray-600 dark:hover:text-gray-300 lg:bg-green-500 lg:p-3 lg:hover:bg-green-600 lg:dark:bg-green-600 lg:dark:hover:bg-green-500"
								>
									<span className="sr-only">Öppna tjänstemenyn</span>
									<Menu
										className="h-7 w-7 text-gray-400 dark:text-gray-400 lg:h-6 lg:w-6 lg:text-white lg:dark:text-white"
										aria-hidden="true"
									/>
								</button>
							</PopoverTrigger>
							<PopoverContent
								align="end"
								sideOffset={8}
								className="w-auto rounded-lg p-0 shadow-lg"
							>
								<ServiceMenu isSignedIn={isSignedIn} authUrl={authUrl} />
							</PopoverContent>
						</Popover>
					</div>
				</div>
			</nav>
			<SearchDialog isOpen={searchOpen} setIsOpen={setSearchOpen} />
		</>
	)
}
